import ReportSectionCard from './shared/ReportSectionCard.component'
import ReportSectionHeading from '@components/pages/Reports/shared/ReportSectionHeading.component'
import React, { useMemo, useState } from 'react'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import ReportBundleStickyTopWhenExpanded from '@components/pages/Reports/shared/ReportBundleStickyTopWhenExpanded.component'
import ReportFilterToolbar from '@components/pages/Reports/shared/ReportFilterToolbar.component'
import ReportKpiGrid from '@components/pages/Reports/shared/ReportKpiGrid.component'
import ReportPageHeader from '@components/pages/Reports/shared/ReportPageHeader.component'
import {
  COMMUNICATION_CENTER_FILTERS,
  COMMUNICATION_CENTER_KPI_DEFINITIONS,
  COMMUNICATION_EFFECTIVENESS_BAR_DEFINITIONS,
  CommunicationEffectivenessRow,
} from '@constants/Reports/communicationCenterPerformance.constants'
import { ReportsKpiMetric } from '@dataTypes/reports.type'
import { useCommunicationCenterPerformance } from '@hooks/reports/useCommunicationCenterPerformance'
import { useReportFinanceUiEffects } from '@hooks/reports/useReportFinanceUiEffects'
import { formatCount } from '@utils/dashboardFinance.util'
import {
  hasNonZeroDashboardValues,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import { formatKpiPercent } from '@utils/reports/revenueRealizationFinance.util'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from '@components/pages/Reports/shared/ReportDesignSystemCharts.component'

const REPORT_CHART_AXIS_TICK = { fontSize: 12, fill: 'var(--gray-500)' }
const REPORT_CHART_TOOLTIP_STYLE = {
  borderRadius: 8,
  border: '1px solid var(--gray-200)',
  fontSize: 12,
}
const REPORT_CHART_GRID_STROKE = 'var(--gray-100)'
/** Legend sits in bottom margin — keep this tight to avoid empty band above card bottom */
const REPORT_LINE_CHART_MARGIN_LEGEND = {
  top: 6,
  right: 12,
  left: 4,
  bottom: 14,
}
/** XAxis `height` holds two-line ticks; keep `bottom` at 0 to avoid double-padding under the plot. */
const REPORT_BAR_CHART_MARGIN_EFFECTIVENESS = {
  top: 10,
  right: 12,
  left: 4,
  bottom: 0,
}
/** Slightly tighter gap so category bars read wider next to multi-line axis labels. */
const REPORT_BAR_CATEGORY_GAP_EFFECTIVENESS = '12%'
const REPORT_YAXIS_WIDTH = 46

function formatHandlingTime(seconds: number): string {
  const total = Math.max(0, Math.round(seconds))
  const minutes = Math.floor(total / 60)
  const secs = total % 60
  return `${minutes}m ${secs}s`
}

function formatShareFooter(
  count: number,
  total: number,
  suffix: string,
): string {
  if (total <= 0) return suffix
  const pct = Math.round((count / total) * 1000) / 10
  return `${pct}% ${suffix}`
}

function computeCountAxis(values: number[]): {
  domain: [number, number]
  ticks: number[]
} {
  const max = values.length ? Math.max(...values, 0) : 0
  const axisMax = max <= 0 ? 10 : Math.ceil((max * 1.15) / 10) * 10
  const step = axisMax <= 20 ? 5 : axisMax <= 50 ? 10 : Math.ceil(axisMax / 4)
  const ticks: number[] = []
  for (let t = 0; t <= axisMax; t += step) ticks.push(t)
  return { domain: [0, axisMax], ticks }
}

function CommunicationEffectivenessXAxisTick(props: {
  x?: number
  y?: number
  payload?: { value?: string }
  rows: CommunicationEffectivenessRow[]
}): React.ReactElement {
  const { x, y, payload, rows } = props
  const row = rows.find((r) => r.id === payload?.value)
  if (row == null || x === undefined || y === undefined) {
    return <g />
  }
  return (
    <text x={x} y={y} textAnchor="middle" fill="var(--gray-500)" fontSize={11}>
      <tspan x={x} dy={9}>
        {row.labelLine1}
      </tspan>
      <tspan x={x} dy={12}>
        {row.labelLine2}
      </tspan>
    </text>
  )
}

function CommunicationCenterPerformanceReport(): React.ReactElement {
  const [reportSaveViewAction, setReportSaveViewAction] =
    useState<JSX.Element | null>(null)
  const [reportGeneratePDFAction, setReportGeneratePDFAction] =
    useState<JSX.Element | null>(null)
  // `reportFiltersAndMetrics` is the source of truth for the filters and metrics of the currently selected report view.
  // Use this for all data queries and any logic that depends on the selected report view.
  const [reportFiltersAndMetrics, setReportFiltersAndMetrics] = useState<{
    filters: Record<string, boolean>
    metrics: Record<string, boolean>
  } | null>(null)
  const { loading, error, data } = useCommunicationCenterPerformance(
    (reportFiltersAndMetrics?.filters ?? {}) as unknown as Record<
      string,
      string
    >,
  )
  useReportFinanceUiEffects({
    loading,
    error,
    errorMessage:
      'Failed to load communication center performance data. Please try again.',
  })

  const kpiItems = useMemo((): ReportsKpiMetric[] => {
    if (!data) {
      return COMMUNICATION_CENTER_KPI_DEFINITIONS.map((def) => ({
        id: def.id,
        title: def.title,
        footer: def.footer,
        unitInline: def.unitInline,
        tone: def.tone,
        value: loading ? '…' : '—',
      }))
    }

    const total = data.totalCalls.value
    const answered = data.answered.value
    const missed = data.missed.value
    const followUps = data.followUps.value

    const valueById: Record<string, string> = {
      'cc-total-calls': formatCount(total),
      'cc-answered': formatCount(answered),
      'cc-missed': formatCount(missed),
      'cc-declined': formatCount(data.declined.value),
      'cc-followups': formatCount(followUps),
      'cc-sentiment': formatKpiPercent(data.sentiment.value),
      'cc-avg-time': formatHandlingTime(data.avgHandlingTimeSeconds.value),
    }

    const footerById: Record<string, string> = {
      'cc-total-calls': formatShareFooter(answered, total, 'answered calls'),
      'cc-answered': 'Primary volume',
      'cc-missed': formatShareFooter(missed, total, 'missed'),
      'cc-declined': 'Needs callbacks',
      'cc-followups': formatShareFooter(followUps, total, 'of calls'),
      'cc-sentiment': data.sentiment.dominantSentiment ?? 'Dominant share',
      'cc-avg-time': 'Per answered call',
    }

    return COMMUNICATION_CENTER_KPI_DEFINITIONS.map((def) => ({
      id: def.id,
      title: def.title,
      footer: footerById[def.id] ?? def.footer,
      unitInline: def.format === 'percent' ? undefined : def.unitInline,
      tone: def.tone,
      value: valueById[def.id] ?? '—',
    }))
  }, [data, loading])

  const callVolumeData = useMemo(() => {
    if (!data) return []
    return data.callVolumeOutcomes.buckets.map((row) => ({
      day: row.label,
      total: row.total,
      answered: row.answered,
      missed: row.missed,
    }))
  }, [data])

  const callVolumeYAxis = useMemo(() => {
    const values = callVolumeData.flatMap((row) => [
      row.total,
      row.answered,
      row.missed,
    ])
    return computeCountAxis(values)
  }, [callVolumeData])

  const messagingData = useMemo(() => {
    if (!data) return []
    return data.messagingPerformance.buckets.map((row) => ({
      day: row.label,
      inbound: row.inbound,
      replied: row.replied,
    }))
  }, [data])

  const messagingYAxis = useMemo(() => {
    const values = messagingData.flatMap((row) => [row.inbound, row.replied])
    return computeCountAxis(values)
  }, [messagingData])

  const effectivenessRows = useMemo((): CommunicationEffectivenessRow[] => {
    const rateById = new Map(
      (data?.communicationEffectiveness.bars ?? []).map((bar) => [
        bar.id,
        bar.rate,
      ]),
    )
    return COMMUNICATION_EFFECTIVENESS_BAR_DEFINITIONS.map((def) => ({
      ...def,
      rate: rateById.get(def.id) ?? 0,
    }))
  }, [data])

  const effectivenessChartData = useMemo(
    () =>
      effectivenessRows.map((row) => ({
        ...row,
        topLabel: `${row.rate.toFixed(2)}%`,
      })),
    [effectivenessRows],
  )

  const kpiEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      data?.totalCalls.value,
      data?.answered.value,
      data?.missed.value,
      data?.declined.value,
      data?.followUps.value,
      data?.sentiment.value,
      data?.avgHandlingTimeSeconds.value,
    ),
  )
  const callVolumeEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.callVolumeOutcomes?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.total, row.answered, row.missed),
    ),
  )
  const messagingEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.messagingPerformance?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.inbound, row.replied),
    ),
  )
  const effectivenessEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.communicationEffectiveness?.bars ?? []).some((row) =>
      hasNonZeroDashboardValues(row.rate),
    ),
  )

  return (
    <div className="reports-patient-base-health reports-treatment-conversion-funnel reports-communication-center-performance">
      <ReportBundleStickyTopWhenExpanded>
        <ReportPageHeader title="Communication Center Performance">
          {reportSaveViewAction}
          {reportGeneratePDFAction}
        </ReportPageHeader>

        <ReportFilterToolbar
          filters={COMMUNICATION_CENTER_FILTERS}
          onReportSaveViewActionChange={setReportSaveViewAction}
          onReportGeneratePDFActionChange={setReportGeneratePDFAction}
          onReportFiltersAndMetricsChange={setReportFiltersAndMetrics}
        />
      </ReportBundleStickyTopWhenExpanded>

      {kpiEmpty ? (
        <DashboardSectionEmptyState />
      ) : (
        <ReportKpiGrid items={kpiItems} />
      )}

      <div className="reports-treatment-conversion-funnel__stack">
        <div className="reports-treatment-conversion-funnel__grid">
          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Calls Volume &amp; Outcomes
                </ReportSectionHeading>
              </div>
              <p className="reports-treatment-conversion-funnel__chart-note">
                Total, answered, and missed by period
              </p>
              {callVolumeEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart reports-communication-center-performance__line-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={callVolumeData}
                      margin={REPORT_LINE_CHART_MARGIN_LEGEND}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                      />
                      <XAxis
                        dataKey="day"
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        domain={callVolumeYAxis.domain}
                        ticks={callVolumeYAxis.ticks}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                      />
                      <Tooltip contentStyle={REPORT_CHART_TOOLTIP_STYLE} />
                      <Legend
                        iconType="circle"
                        iconSize={8}
                        wrapperStyle={{ fontSize: 12, paddingTop: 4 }}
                      />
                      <Line
                        type="linear"
                        dataKey="total"
                        name="Total"
                        stroke="var(--blue-500)"
                        strokeWidth={2}
                        dot={false}
                        activeDot={false}
                      />
                      <Line
                        type="linear"
                        dataKey="answered"
                        name="Answered"
                        stroke="var(--vivid-green-400)"
                        strokeWidth={2}
                        dot={false}
                        activeDot={false}
                      />
                      <Line
                        type="linear"
                        dataKey="missed"
                        name="Missed"
                        stroke="var(--orange-400)"
                        strokeWidth={2}
                        dot={false}
                        activeDot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </ReportSectionCard>

          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Messaging Performance
                </ReportSectionHeading>
              </div>
              <p className="reports-treatment-conversion-funnel__chart-note">
                Inbound SMS vs replies by period
              </p>
              {messagingEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart reports-communication-center-performance__line-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={messagingData}
                      margin={REPORT_LINE_CHART_MARGIN_LEGEND}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                      />
                      <XAxis
                        dataKey="day"
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        domain={messagingYAxis.domain}
                        ticks={messagingYAxis.ticks}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                      />
                      <Tooltip contentStyle={REPORT_CHART_TOOLTIP_STYLE} />
                      <Legend
                        iconType="circle"
                        iconSize={8}
                        wrapperStyle={{ fontSize: 12, paddingTop: 4 }}
                      />
                      <Line
                        type="linear"
                        dataKey="inbound"
                        name="Inbound"
                        stroke="var(--blue-500)"
                        strokeWidth={2}
                        dot={false}
                        activeDot={false}
                      />
                      <Line
                        type="linear"
                        dataKey="replied"
                        name="Replied"
                        stroke="var(--vivid-green-400)"
                        strokeWidth={2}
                        dot={false}
                        activeDot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </ReportSectionCard>
        </div>

        <div className="reports-treatment-conversion-funnel__grid reports-communication-center-performance__effectiveness-row">
          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Communication Effectiveness
                </ReportSectionHeading>
              </div>
              <p className="reports-treatment-conversion-funnel__chart-note">
                Channel and campaign outcome rates (period)
              </p>
              {effectivenessEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart reports-communication-center-performance__effectiveness-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={effectivenessChartData}
                      margin={REPORT_BAR_CHART_MARGIN_EFFECTIVENESS}
                      barCategoryGap={REPORT_BAR_CATEGORY_GAP_EFFECTIVENESS}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                      />
                      <XAxis
                        dataKey="id"
                        axisLine={false}
                        tickLine={false}
                        interval={0}
                        tick={(tickProps) => (
                          <CommunicationEffectivenessXAxisTick
                            {...tickProps}
                            rows={effectivenessRows}
                          />
                        )}
                        height={36}
                      />
                      <YAxis
                        domain={[0, 100]}
                        ticks={[0, 20, 40, 60, 80, 100]}
                        tickFormatter={(v) => `${v}%`}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                      />
                      <Tooltip
                        formatter={(
                          value: number,
                          _name: string,
                          item: { payload?: { label?: string } },
                        ) => [`${value}%`, item.payload?.label ?? 'Rate']}
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Bar
                        dataKey="rate"
                        fill="var(--blue-400)"
                        barSize={100}
                        radius={[8, 8, 8, 8]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </ReportSectionCard>
        </div>
      </div>
    </div>
  )
}

export default CommunicationCenterPerformanceReport
